<template>
    <NebulaModal v-if="welcomeStore.show" class="welcome-modal" size="m" @close-modal="closeTopModal">
        <template #content>
            <!-- Loading State -->
            <div v-if="loading" class="welcome-modal__loading">
                <CircleLoader />
                <p>Loading tour content...</p>
            </div>

            <!-- Empty State: no slides returned, or every slide filtered out -->
            <NebulaBlockMessage
                v-else-if="hasNoContent"
                class="welcome-modal__error"
                icon="warning--filled"
                :header="$t('Unable to Load Content')"
                color="orange"
                :description="$t('Welcome tour content is not available. Please try again later or contact support if the issue persists.')"
            />

            <!-- Main Content -->
            <div v-if="!loading && modalContent.slides.length > 0" class="welcome-modal__column-inline-start">
                <div class="welcome-modal__column-inner">
                    <div class="welcome-modal__row">
                        <h1 class="welcome-modal__heading-1">{{ $t(modalContent.title) }}</h1>
                        <div role="tablist" :aria-label="$t('Tabs Navigation')" class="welcome-modal__tab-list">
                            <NebulaButton
                                v-for="(slide, index) in modalContent.slides"
                                :ref="`tab-${index}`"
                                :aria-controls="`panel-${index}`"
                                :aria-selected="index === currentSlideIndex"
                                :class="['welcome-modal__tab-list-item', { 'welcome-modal__tab-list-item--selected': index === currentSlideIndex }]"
                                :data-click-name="`Tab: ${slide.listItemTitle}`"
                                :data-click-type="`${userRole} page: welcome modal`"
                                :id="`tab-${index}`"
                                :key="index"
                                role="tab"
                                size="s"
                                tabindex="0"
                                :text="slide.listItemTitle"
                                type="flat"
                                @click="changeToSlide(index)"
                                @keydown="handleKeydown"
                            />
                        </div>
                    </div>
                    <div class="welcome-modal__row welcome-modal__row-stack">
                        <NebulaButton
                            class="welcome-modal__next-button"
                            aria-label="Next"
                            :data-click-name="'Next'"
                            :data-click-type="`${userRole} page: welcome modal`"
                            @click="videoStepperClick"
                            :text="$t('Next')"
                            type="primary"
                            size="s"
                        />
                    </div>
                </div>
            </div>
            <div v-if="!loading && modalContent.slides.length > 0" class="welcome-modal__column-inline-end">
                <div v-for="(slide, index) in modalContent.slides" :key="index">
                    <div
                        v-show="index === currentSlideIndex"
                        :aria-hidden="index !== currentSlideIndex"
                        :aria-label="`${slide.listItemTitle}`"
                        :id="`panel-${index}`"
                        role="tabpanel"
                        aria-orientation="vertical"
                    >
                        <div class="welcome-modal__video-player-container">
                            <video
                                v-if="slide.videoUrl"
                                v-show="!videoErrorStates[index]"
                                :ref="`video-${index}`"
                                class="welcome-modal__video"
                                :src="slide.videoUrl"
                                @play="onVideoPlay(index)"
                                @pause="onVideoPause(index)"
                                @ended="onVideoEnded(index)"
                                @loadeddata="onVideoLoaded(index)"
                                @error="onVideoError(index, $event)"
                            >
                                <track kind="captions" label="English" srclang="en" />
                            </video>
                            <NebulaButton
                                v-if="hasPlayableVideo(slide, index)"
                                :aria-label="videoAudioStates[index] ? $t('Mute video') : $t('Unmute video')"
                                class="welcome-modal__video-control welcome-modal__video-control--audio"
                                :data-click-name="videoAudioStates[index] ? 'Mute' : 'Unmute'"
                                :data-click-type="`${userRole} page: welcome modal`"
                                :icon-left="videoAudioStates[index] ? 'volume-high--filled' : 'mute--filled'"
                                size="xs"
                                type="pill"
                                @click="videoPlayer.toggleVideoMute($refs, index)"
                            />
                            <NebulaButton
                                v-if="hasPlayableVideo(slide, index)"
                                :aria-label="videoPausedStates[index] ? $t('Play video') : $t('Pause video')"
                                class="welcome-modal__video-control"
                                :data-click-name="videoPausedStates[index] ? 'Play' : 'Pause'"
                                :data-click-type="`${userRole} page: welcome modal`"
                                :icon-left="videoPausedStates[index] ? 'play--filled' : 'pause'"
                                size="xs"
                                type="pill"
                                @click="videoPlayer.toggleVideoPlayback($refs, index)"
                            />
                            <div v-else class="welcome-modal__video-state welcome-modal__video-state--error" role="status">
                                {{ videoStateMessage(index) }}
                            </div>
                        </div>
                        <div class="welcome-modal__content-container">
                            <!-- Content from trusted internal CMS (Discovery Education API) -->
                            <!-- v-html required for formatted text (bold, links, etc.) -->
                            <h3 v-if="slide.subtitle" v-html="slide.subtitle" class="welcome-modal__heading-3" />
                            <h2 v-html="slide.title" class="welcome-modal__heading-2" />
                            <p v-html="slide.description" class="welcome-modal__text" />
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </NebulaModal>
</template>

<script>
import { NebulaButton, NebulaModal, NebulaBlockMessage } from '@discoveryedu/nebula-components';
import useWelcomeModalStore from '@/stores/useWelcomeModalStore';
import CircleLoader from '@/components/shared/Loader/CircleLoader.vue';
import { useAppStore } from '@/stores/useAppStore';
import { useVideoPlayer } from '@/composables/useVideoPlayer';
import { transformSlidesToModalContent } from '@/utils/welcomeModal/transformSlides';
import { decodeHtml } from '@/utils/htmlHelpers';

export default {
    name: 'WelcomeModal',

    components: {
        CircleLoader,
        NebulaBlockMessage,
        NebulaButton,
        NebulaModal,
    },

    setup() {
        const videoPlayer = useVideoPlayer();
        const appStore = useAppStore();
        return { videoPlayer, appStore };
    },

    data() {
        return {
            currentSlideIndex: 0,
            modalContent: {
                title: '',
                subtitle: '',
                slides: [],
            },
        };
    },

    computed: {
        hasNoContent() {
            return !this.loading && this.modalContent.slides.length === 0;
        },
        isLastSlide() {
            return this.modalContent.slides.length > 0 && this.currentSlideIndex === this.modalContent.slides.length - 1;
        },
        loading() {
            return this.welcomeStore.loading;
        },
        slides() {
            return this.welcomeStore.slides;
        },
        userRole() {
            return this.appStore.appMode;
        },
        videoPausedStates() {
            return this.videoPlayer.videoPausedStates.value;
        },
        videoAudioStates() {
            return this.videoPlayer.videoAudioStates.value;
        },
        videoErrorStates() {
            return this.videoPlayer.videoErrorStates.value;
        },
        welcomeStore() {
            return useWelcomeModalStore();
        },
    },

    watch: {
        'welcomeStore.show': {
            immediate: true,
            async handler(isShowing) {
                if (isShowing) {
                    this.currentSlideIndex = 0;

                    if (!this.welcomeStore.slides && !this.welcomeStore.loading) {
                        try {
                            await this.welcomeStore.loadSlides(this.userRole);
                        } catch (error) {
                            console.error('Failed to load welcome modal:', error);
                        }
                    }

                    // Focus the first tab for keyboard accessibility when modal opens
                    this.$nextTick(() => {
                        this.focusTab(0);
                    });
                }
            },
        },
        slides: {
            immediate: true,
            handler(slides) {
                this.modalContent = transformSlidesToModalContent(slides);

                // Auto-play first video when content is loaded
                if (slides && slides.length > 0) {
                    this.$nextTick(() => {
                        this.videoPlayer.playVideo(this.$refs, 0, true);
                    });
                }
            },
        },
    },

    mounted() {
        window.addEventListener('keydown', this.handleGlobalKeydown);
    },

    beforeUnmount() {
        window.removeEventListener('keydown', this.handleGlobalKeydown);
        this.welcomeStore.clearSlides();
    },

    methods: {
        changeToSlide(index) {
            const decodedTitle = decodeHtml(this.modalContent.slides[index].title);
            const trackingName = `Next: ${decodedTitle}`;
            this.trackingEvent(trackingName);

            // Pause ALL videos before switching to prevent audio bleed
            this.videoPlayer.pauseAllVideos(this.$refs, this.modalContent.slides, true);

            // Update slide
            this.currentSlideIndex = index;

            // Play the new video after switching
            this.videoPlayer.playVideo(this.$refs, index, true);
        },

        closeTopModal() {
            this.welcomeStore.closeModal();
        },
        getTabElement(index) {
            const tabRef = this.$refs[`tab-${index}`];
            const element = Array.isArray(tabRef) ? tabRef[0] : tabRef;
            // NebulaButton is a Vue component, so we need to access its DOM element via $el
            return element?.$el;
        },
        focusTab(index) {
            this.$nextTick(() => {
                const tabElement = this.getTabElement(index);
                if (tabElement) {
                    tabElement.focus();
                }
            });
        },
        handleTabNavigation(event, currentIndex, tabCount, changeCallback) {
            let targetIndex;

            switch (event.key) {
                case 'ArrowRight':
                case 'ArrowDown':
                    // Move to next tab, or wrap to first
                    targetIndex = (currentIndex + 1) % tabCount;
                    break;
                case 'ArrowLeft':
                case 'ArrowUp':
                    // Move to previous tab, or wrap to last
                    targetIndex = (currentIndex - 1 + tabCount) % tabCount;
                    break;
                default:
                    return;
            }

            event.preventDefault();

            // Call the change callback (e.g., changeToSlide)
            if (changeCallback) {
                changeCallback(targetIndex);
            }

            // Focus on target tab
            this.focusTab(targetIndex);
        },

        handleGlobalKeydown(event) {
            // Only handle space key when the modal is showing
            if (!this.welcomeStore.show) {
                return;
            }

            if (event.key === ' ' || event.code === 'Space') {
                event.preventDefault();
                this.videoPlayer.toggleVideoPlayback(this.$refs, this.currentSlideIndex);
            }
        },
        handleKeydown(event) {
            this.handleTabNavigation(event, this.currentSlideIndex, this.modalContent.slides.length, this.changeToSlide);
        },
        hasPlayableVideo(slide, index) {
            return Boolean(slide.videoUrl) && !this.videoErrorStates[index];
        },
        videoStateMessage(index) {
            // A missing URL and a URL that fails to load are different situations:
            // the first has no video to offer, the second may recover on a reload.
            if (this.videoErrorStates[index]) {
                return this.$t('This video could not be loaded. Please refresh the page to try again.');
            }

            return this.$t('Video content not available');
        },
        onVideoLoaded(index) {
            this.videoPlayer.onVideoLoaded(this.$refs, index, this.currentSlideIndex);
        },
        onVideoError(index, event) {
            this.videoPlayer.onVideoError(index, event);

            // A failing source is usually a cached DE media URL that has gone stale, so
            // drop the cache and refetch once. The slides watcher re-renders on success.
            this.welcomeStore.retryStaleSlides(this.userRole);
        },
        onVideoPause(index) {
            this.videoPlayer.onVideoPause(index);
        },
        onVideoPlay(index) {
            this.videoPlayer.onVideoPlay(index);
        },
        onVideoEnded(index) {
            this.videoPlayer.onVideoEnded(index);
        },
        trackingEvent(trackingName) {
            if (window.DEAnalytics) {
                window.DEAnalytics.linkClicked({
                    type: `${this.userRole} page: welcome modal`,
                    name: trackingName,
                });
            }
        },
        videoStepperClick() {
            const nextIndex = !this.isLastSlide ? this.currentSlideIndex + 1 : 0;
            this.changeToSlide(nextIndex);
        },
    },
};
</script>

<style lang="stylus">
.welcome-modal {
    display: flex;

    .nebula-modal--m .nebula-modal__inner {
        padding: 0;
    }

    .nebula-modal__content-container {
        display: flex;
        max-height: calc(100vh - 120px); // Constrain modal to viewport height
        overflow: hidden; // Hide overflow on container
    }

    // Ensure the modal scroll container allows overflow for X button
    .nebula-modal__scroll-container {
        overflow: visible;
    }

    .nebula-modal__scroll-container--m {
        max-width: 850px;
    }

    // Position close button above modal body
    .nebula-modal__x-button-container {
        position: absolute;
        right: -25px;
        top: -60px;

        .nebula-button {
            &:hover {
                background: transparent;
            }
        }

        .nebula-button__icon {
            fill: white;
        }

        .nebula-button:hover .nebula-button__icon {
            fill: white;
        }
    }

    .nebula-modal-overlay {
        backdrop-filter: none;
        background: rgba(20, 28, 31, 0.65)
        pointer-events: none; // Disable closing on overlay click
        z-index: 0;
    }

    &__column-inline-end {
        display: flex;
        flex: 2;
        flex-direction: column;
        max-width: 600px;
        padding: 0 0 $nebula-space-3x;
        border-radius: 0 $nebula-border-radius-small 0 0;
        overflow-y: auto; // Enable vertical scrolling
        overflow-x: hidden;
    }

    &__column-inline-start {
        background: linear-gradient(90deg, rgba(0, 0, 0, 0) 95%, rgba(0, 0, 0, .06) 100%);
        flex: 1;
        max-width: 250px;
        padding: $nebula-space-2x $nebula-space-2x $nebula-space-3x;
        overflow-y: auto; // Enable vertical scrolling
        overflow-x: hidden;
    }

    &__column-inner {
        display: flex;
        flex-direction: column;
        height: 100%;
        justify-content: space-between;
    }

    &__content-container {
        display: flex;
        flex-direction: column;
        min-height: 200px; // Changed from fixed height to min-height
        padding: $nebula-space-2x 0;
    }

    &__error {
        margin: $nebula-space-5x;
    }

    &__heading-1 {
        color: $nebula-color-platform-neutral-1000;
        font-size: 1.25rem;
        margin-block-end: $nebula-space-3x;
        nebula-text-h6();
    }

    &__heading-2 {
        color: $nebula-color-platform-neutral-1000;
        line-height: 1.5rem;
        margin-inline: $nebula-space-2x;
        margin-bottom: $nebula-space-half;
        nebula-text-body-1();
        font-weight: 700;
    }

    &__heading-3 {
        color: $nebula-color-platform-neutral-700;
        font-size: $nebula-font-size-caption;
        margin-bottom: $nebula-space-1x;
        margin-inline: $nebula-space-2x;
        nebula-text-body-2();
    }

    &__link {
        font-size: $nebula-font-size-caption;
        margin-inline: $nebula-space-2x;
        nebula-text-link();
    }

    &__loading {
        align-items: center;
        display: flex;
        flex-direction: column;
        gap: $nebula-space-2x;
        justify-content: center;
        min-height: 300px;
        padding: $nebula-space-5x;
        text-align: center;

        p {
            color: $nebula-color-platform-neutral-800;
            nebula-text-body-2();
        }
    }

    &__next-button {
        justify-content: space-around;
        width: 100%;
    }

    &__row-stack {
        display: flex;
        flex-direction: column;
        gap: $nebula-space-2x;

        .nebula-button--s {
            width: 100%;
        }
    }

    &__tab-list-item.nebula-button--s.nebula-button--flat {
        --nebula-button-active: $nebula-color-platform-interactive-400;
        --nebula-button-background-color: transparent;
        --nebula-button-border-radius: $nebula-border-radius-small;
        --nebula-button-font-size: $nebula-font-size-body-2;
        --nebula-button-hover: $nebula-color-platform-interactive-200;
        --nebula-button-padding: $nebula-space-1x 11px;
        --nebula-button-text-color: $nebula-color-platform-interactive-1000;
        --nebula-button-text-color-hover: $nebula-color-platform-interactive-1000;
        --nebula-button-text-color-active: $nebula-color-platform-interactive-1000;

        margin-block-end: $nebula-space-1x;
        width: 100%;
    }

    &__tab-list-item--selected.nebula-button--s.nebula-button--flat {
        --nebula-button-active: $nebula-color-platform-interactive-400;
        --nebula-button-hover: $nebula-color-platform-interactive-400;
        background-color: $nebula-color-platform-interactive-400;

        &:hover {
            --nebula-button-active: $nebula-color-platform-interactive-400;
            --nebula-button-hover: $nebula-color-platform-interactive-400;
        }
    }

    &__text {
        color: $nebula-color-platform-neutral-800;
        font-size: $nebula-font-size-caption;
        margin-inline: $nebula-space-2x;
        margin-top: $nebula-space-half;
        nebula-text-body-2();
    }

    &__video {
        height: 100%;
        width: 100%;
    }

    &__video-control {
        background: $nebula-platform-interactive-850;
        cursor: pointer;
        inset-block-end: $nebula-space-2x;
        inset-inline-end: $nebula-space-2x;
        position: absolute;

        &--audio {
            inset-inline-end: 50px;
        }
    }

    &__video-player-container {
        background-color: $nebula-color-white;
        box-shadow: inset 0 2px 16px #a2bbc3a0;
        height: 337px;
        position: relative;
        width: 100%;
    }

    &__video-state {
        left: 50%;
        max-width: 80%;
        nebula-text-h6()
        position: absolute;
        text-align: center;
        top: 50%;
        transform: translate(-50%, -50%);

        &--error {
            color: $nebula-color-error;
        }
    }
}
</style>
